import {
  IndexTable,
  Card,
  Text,
  Link,
  Avatar,
  InlineStack,
  Tooltip,
  BlockStack,
  EmptyState,
  Pagination,
  Select,
  Icon,
  DatePicker,
  Modal,
  Box,
  InlineGrid,
  Button,
  Spinner
} from '@shopify/polaris';
import {
  CursorFilledIcon,
  CashDollarIcon,
  QuestionCircleIcon,
  PaymentIcon,
  DatabaseIcon,
  OrderIcon,
  PersonIcon,
  ArchiveIcon,
  ChartFunnelIcon,
  ImagesIcon,
  SmileyJoyIcon
} from '@shopify/polaris-icons'

import { useCallback, useEffect, useState } from 'react';
import { getBestAvatar, getDaysBetweenDates, getMonthDayString, numberToUSD, shortenNumber } from '../utils';
import AppLayout from '../components/layout';
import { useNavigate } from 'react-router-dom';
import { usePartnerUp } from '../providers/PartnerUpStoreProvider';
import PerformanceGraph from '../components/PerformanceGraph';
import CategoryCards from '../components/CategoryCards'

function renderHeading(title, icon) {
  return (
    <InlineStack key={title} align="start" gap="100">
      {
        icon ? (
          <InlineStack>
            <Icon source={icon} color="subdued" />
          </InlineStack>
        ) : null
      }
      <Text>{title}</Text>
    </InlineStack>
  )
}

export default function AffiliatePaymentsTable() {
  const { allCodes, content, deals, campaigns, creatorPosts, pastPayments, leads, store } = usePartnerUp();
  const [activeTab, setActiveTab] = useState(0)
  const [dateRange, setDateRange] = useState('last30')
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [typeTab, setTypeTab] = useState(0)
  const [selectedCampaign, setSelectedCampaign] = useState('all')

  const navigate = useNavigate();

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const [{ month, year }, setDate] = useState({ month: currentMonth, year: currentYear });

  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  const [selectedDates, setSelectedDates] = useState({
    start: new Date(oneWeekAgo),
    end: new Date(),
  });

  const handleMonthChange = useCallback(
    (month, year) => setDate({ month, year }),
    [],
  );

  useEffect(() => {
    if (dateRange === 'custom') {
      setDatePickerOpen(true)
    } else if (dateRange === 'allTime') {
      setSelectedDates({
        start: new Date('2024-01-01'),
        end: new Date(),
      })
    } else if (dateRange === 'last7') {
      setSelectedDates({
        start: new Date(oneWeekAgo),
        end: new Date(),
      })
    } else if (dateRange === 'last30') {
      const oneMonthAgo = new Date();
      oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
      setSelectedDates({
        start: oneMonthAgo,
        end: new Date(),
      })
    } else if (dateRange === 'last90') {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setDate(threeMonthsAgo.getDate() - 90);
      setSelectedDates({
        start: threeMonthsAgo,
        end: new Date(),
      })
    }
  }, [dateRange])


  const resourceName = {
    singular: 'creator',
    plural: 'creators',
  };
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /*
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(Object.values(ordersByCreator).map(({ orders }) => orders).flat());
*/

  /*
  const sortedPastPayments = pastPayments?.sort((a, b) => {
    return new Date(b.timestamp) - new Date(a.timestamp)
  })
*/

  useEffect(() => {
    setPage(0)
  }, [activeTab])

  if (!leads?.length) {
    return <Spinner />
  }

  const allMap = {
    orders: 0,
    clicks: 0,
    revenue: 0,
    posts: 0,
    cost: {
      commission: 0,
      shipping: 0,
      gift: 0,
      fixed: 0,
    }
  }

  const engagements = creatorPosts?.reduce((a, post) => {
    if (selectedCampaign !== 'all' && post.campaignID !== selectedCampaign) {
      return a
    }
    return a + (post.stats?.likes || 0) + (post.stats?.comments || 0)
  }, 0)

  const flatOrders = [];
  const campaignsMap = campaigns?.reduce((a, campaign) => {
    a[campaign.id] = {
      ...campaign,
      flatOrders: [],
      giftOrders: [],
      fixedPayments: [],
    }
    return a;
  }, {}) || {}

  const leadsMap = leads?.reduce((a, lead) => {
    if (!a[lead.id]) {
      a[lead.id] = {
        creatorID: lead.id,
        creator: lead,
        flatOrders: [],
        giftOrders: [],
        fixedPayments: [],
      }
    }
    return a
  }, {})

  allCodes?.forEach((code) => {
    if (selectedCampaign !== 'all' && code.campaignID !== selectedCampaign) {
      return
    }
    if (leadsMap[code.creatorID]) {
      const orders = code.orders;
      if (orders?.length) {
        orders.forEach(order => {
          const commissionPayment = code.commission ? ((code.commission / 100) * order.subtotalPriceSet?.shopMoney?.amount) : 0

          const orderDate = new Date(order?.processedAt)
          if (orderDate >= selectedDates.start && orderDate <= selectedDates.end) {
            allMap.orders += 1
            allMap.cost.commission += commissionPayment
            allMap.revenue += Number(order.subtotalPriceSet?.shopMoney?.amount) || 0
          }

          if (campaignsMap[code.campaignID]) {
            campaignsMap[code.campaignID].flatOrders.push({
              ...order,
              commissionPayment,
            })
          }
          flatOrders.push({
            ...order,
            commissionPayment,
            creatorID: code.creatorID,
            code: code.code
          });
          leadsMap[code.creatorID].flatOrders.push({
            ...order,
            commissionPayment,
          })
        })
      }
    }
  }, {})

  deals?.forEach(deal => {
    if (selectedCampaign !== 'all' && deal.campaignID !== selectedCampaign) {
      return
    }
    const orders = []
    if (deal?.order) {
      orders.push(deal.order)
    }
    if (deal?.additionalOrders?.length) {
      orders.push(...deal.additionalOrders)
    }
    orders?.forEach(order => {
      if (leadsMap[deal.creatorID]) {
        leadsMap[deal.creatorID].giftOrders.push(order)
      }
      if (campaignsMap[deal.campaignID]) {
        campaignsMap[deal.campaignID].giftOrders.push(order)
      }
      if (order?.id && new Date(order.processedAt) >= selectedDates.start && new Date(order.processedAt) <= selectedDates.end) {
        allMap.cost.gift += Number(order?.totalCost || 0)
        allMap.cost.shipping += Number(order?.shippingCost || 0)
      }
    })
  })

  pastPayments?.forEach(payment => {
    if (selectedCampaign !== 'all' && payment.campaignID !== selectedCampaign) {
      return
    }
    if (payment.paymentType === 'fixed' && payment.processed) {
      if (new Date(payment.createdAt) >= selectedDates.start && new Date(payment.createdAt) <= selectedDates.end) {
        allMap.cost.fixed += Number(payment.value)
      }
      if (leadsMap[payment.creatorID]) {
        leadsMap[payment.creatorID].fixedPayments.push(payment)
      }
      if (campaignsMap[payment.campaignID]) {
        campaignsMap[payment.campaignID].fixedPayments.push(payment)
      }
    }
  })

  const leadsArray = Object.values(leadsMap)
  const campaignsArray = Object.values(campaignsMap)

  // and pastPayments
  let rowMarkup = []
  let performanceGraph;
  let headings;
  if (activeTab === 0 || activeTab === 1) {
    rowMarkup = []
    headings = [
      activeTab === 0 ? { title: "Creator", icon: PersonIcon } : { title: "Campaign", icon: ArchiveIcon },
      {
        title: 'Clicks',
        icon: CursorFilledIcon
      },
      {
        title: 'Posts',
        icon: ImagesIcon
      },
      {
        title: 'Revenue',
        icon: CashDollarIcon
      },
      {
        title: 'Total Cost',
        icon: PaymentIcon
      },
      {
        title: 'Profit',
        icon: DatabaseIcon
      },
      {
        title: 'Orders',
        icon: OrderIcon
      },
    ];
    const timeSeriesDates = getDaysBetweenDates(selectedDates.start, selectedDates.end);
    const creatorTimeSeriesOrders = {};


    (activeTab === 0 ? leadsArray : campaignsArray).map(lead => {
      const key = activeTab === 0 ? lead.creatorID : lead.id
      // filter each subarray
      const startDate = selectedDates.start
      const endDate = selectedDates.end
      // does row have orders in this date range
      //
      if (!creatorTimeSeriesOrders[key]) {
        creatorTimeSeriesOrders[key] = {
          creator: lead,
          campaignTitle: lead.campaignTitle,
          revenueTotal: 0,
          costTotal: 0,
          clicksTotal: 0,
          postsTotal: 0,
          engagementsTotal: 0,
          clicksTimeSeries: Array(timeSeriesDates.length).fill(0),
          revenueTimeSeries: Array(timeSeriesDates.length).fill(0),
          costTimeSeries: Array(timeSeriesDates.length).fill(0),
          postsTimeSeries: Array(timeSeriesDates.length).fill(0),
          engagementsTimeSeries: Array(timeSeriesDates.length).fill(0),
        }
      }

      const rangeData = {
        ...lead,
        flatOrders: lead.flatOrders.filter(order => {
          const orderDate = new Date(order?.processedAt)
          return orderDate >= startDate && orderDate <= endDate
        }),
        giftOrders: lead.giftOrders.filter(order => {
          const orderDate = new Date(order?.processedAt)
          return orderDate >= startDate && orderDate <= endDate
        }),
        fixedPayments: lead.fixedPayments.filter(payment => {
          const paymentDate = new Date(payment?.createdAt)
          return paymentDate >= startDate && paymentDate <= endDate
        })
      }

      rangeData.valueTotal = rangeData.flatOrders.reduce((a, order) => {
        const orderDateString = getMonthDayString(new Date(order?.processedAt))
        const dateIndex = timeSeriesDates.indexOf(orderDateString)
        creatorTimeSeriesOrders[key].revenueTimeSeries[dateIndex] += (Number(order?.subtotalPriceSet?.shopMoney?.amount || 0))
        creatorTimeSeriesOrders[key].revenueTotal += (Number(order?.subtotalPriceSet?.shopMoney?.amount || 0))
        return a + Number(order?.subtotalPriceSet?.shopMoney?.amount || 0)
      }, 0)
      rangeData.fixedPaymentsTotal = rangeData.fixedPayments.reduce((a, payment) => {
        creatorTimeSeriesOrders[key].costTimeSeries[timeSeriesDates.indexOf(getMonthDayString(new Date(payment?.createdAt)))] += Number(payment?.value || 0)
        creatorTimeSeriesOrders[key].costTotal += Number(payment?.value || 0)
        return a + Number(payment?.value)
      }, 0)
      rangeData.shippingCostTotal = rangeData.giftOrders.reduce((a, order) => {
        creatorTimeSeriesOrders[key].costTimeSeries[timeSeriesDates.indexOf(getMonthDayString(new Date(order?.processedAt)))] += Number(order?.shippingCost || 0)
        creatorTimeSeriesOrders[key].costTotal += Number(order?.shippingCost || 0)
        return a + Number(order?.shippingCost || 0)
      }, 0)
      rangeData.productCostTotal = rangeData.giftOrders.reduce((a, order) => {
        creatorTimeSeriesOrders[key].costTimeSeries[timeSeriesDates.indexOf(getMonthDayString(new Date(order?.processedAt)))] += Number(order?.totalCost || 0)
        creatorTimeSeriesOrders[key].costTotal += Number(order?.totalCost || 0)
        return a + Number(order?.totalCost || 0)
      }, 0)
      rangeData.commissionTotal = rangeData.flatOrders.reduce((a, order) => {
        creatorTimeSeriesOrders[key].costTimeSeries[timeSeriesDates.indexOf(getMonthDayString(new Date(order?.processedAt)))] += Number(order?.commissionPayment || 0)
        creatorTimeSeriesOrders[key].costTotal += Number(order?.commissionPayment || 0)
        return a + Number(order?.commissionPayment || 0)
      }, 0)

      rangeData.totalClicks = 0;

      const creatorCodes = allCodes?.filter(code => {
        if (activeTab === 0) {
          return code.creatorID === lead.creatorID && creatorTimeSeriesOrders[code.creatorID]
        } else {
          return code.campaignID === lead.id && creatorTimeSeriesOrders[code.campaignID]
        }
      })
      creatorCodes?.forEach(code => {
        const clicksMap = {}
        Object.entries(code?.clicksByDate || {}).forEach(([key, value]) => {
          clicksMap[getMonthDayString(new Date(key))] = value
        })
        timeSeriesDates.forEach((date, idx) => {
          const value = clicksMap[date] || 0
          if (value) {
            creatorTimeSeriesOrders[key].clicksTimeSeries[idx] += value
            creatorTimeSeriesOrders[key].clicksTotal += value
            rangeData.totalClicks += value
            allMap.clicks += value
          }
        })
      })

      rangeData.totalPosts = 0;

      const posts = creatorPosts?.filter(post => {
        if (selectedCampaign !== 'all' && post.campaignID !== selectedCampaign) {
          return false
        }
        return lead?.creatorID === post?.creatorID
      })
      posts?.forEach(p => {
        const postDate = new Date((p.timestamp * 1000))
        const postDateString = getMonthDayString(postDate)
        const dateIndex = timeSeriesDates.indexOf(postDateString)
        creatorTimeSeriesOrders[key].postsTimeSeries[dateIndex] += 1
        creatorTimeSeriesOrders[key].postsTotal += 1
        rangeData.totalPosts += 1
        allMap.posts += 1

        rangeData.totalEngagements += (p.stats?.likes || 0) + (p.stats?.comments || 0) + (p.stats?.plays || p.stats?.views || 0)
        creatorTimeSeriesOrders[key].engagementsTotal += (p.stats?.likes || 0) + (p.stats?.comments || 0) + (p.stats?.plays || p.stats?.views || 0)

        if (p?.stats?.engagementTimeSeries) {
          Object.keys(p.stats.engagementTimeSeries)?.sort((a, b) => {
            return new Date(a) - new Date(b)
          }).forEach((date, i, array) => {
            const dateString = getMonthDayString(new Date(date))
            const dateIndex = timeSeriesDates.indexOf(dateString)
            console.log('dateString', dateString)
            if (timeSeriesDates.includes(dateString)) {
              const prevDate = i !== 0 ? array[i - 1] : null
              const prevDateValue = i !== 0 ? p.stats.engagementTimeSeries[prevDate] || 0 : 0;
              const dateValue = p.stats.engagementTimeSeries[date] || 0
              const curEngagements = (dateValue?.comments || 0) + (dateValue?.likes || 0) + (dateValue?.plays || dateValue?.views || 0)
              const prevEngagements = (prevDateValue?.comments || 0) + (prevDateValue?.likes || 0) + (prevDateValue?.plays || prevDateValue?.views || 0)
              const difference = curEngagements - prevEngagements
              console.log("DIFF", difference)
              const engagementsCount = difference > 0 ? difference : 0
              if (creatorTimeSeriesOrders[key].engagementsTimeSeries[dateIndex]) creatorTimeSeriesOrders[key].engagementsTimeSeries[dateIndex] += engagementsCount
              else creatorTimeSeriesOrders[key].engagementsTimeSeries[dateIndex] = engagementsCount
            }
          })
        }
      })

      return rangeData
    })
      .sort((a, b) => {
        return b.valueTotal - a.valueTotal
      })
      .forEach(
        (
          lead, idx
        ) => {
          const creator = lead.creator
          const totalClicks = lead.totalClicks
          const totalPosts = lead.totalPosts
          const valueTotal = lead.valueTotal
          const fixedPaymentsTotal = lead.fixedPaymentsTotal
          const shippingCostTotal = lead.shippingCostTotal
          const productCostTotal = lead.productCostTotal
          const commissionTotal = lead.commissionTotal

          const numOrders = lead.flatOrders.length;
          const rowProfit = valueTotal - fixedPaymentsTotal - shippingCostTotal - productCostTotal - commissionTotal

          if (rowProfit !== 0 || totalPosts !== 0) { // exclude leads with no cost revenue or posts
            rowMarkup.push(
              <IndexTable.Row
                id={idx}
                key={idx}
                // selected={selectedResources.includes(idx)}
                position={idx}
              >
                {
                  activeTab === 0 ? (
                    <IndexTable.Cell>
                      <InlineStack gap="200" blockAlign="center" align="start">
                        <Avatar size="small" shape="square" source={getBestAvatar(creator)} initials={creator?.name?.[0]} />
                        <Link removeUnderline onClick={() => navigate('/creators/' + creator.id)} variant="bodyMd" fontWeight="bold" as="span">
                          {`${creator?.name}`}
                        </Link>
                      </InlineStack>
                    </IndexTable.Cell>
                  ) : (
                    <IndexTable.Cell>
                      <InlineStack gap="200" blockAlign="center" align="start">
                        <Link removeUnderline onClick={() => {
                          onCampaignClick('/campaigns/' + lead.id)
                        }} variant="bodyMd" fontWeight="bold" as="span">
                          {`${lead.campaignTitle}`}
                        </Link>
                      </InlineStack>
                    </IndexTable.Cell>
                  )
                }
                <IndexTable.Cell><Text color="success">{totalClicks}</Text></IndexTable.Cell>
                <IndexTable.Cell><Text color="success">{totalPosts}</Text></IndexTable.Cell>
                <IndexTable.Cell><Text color="success">{numberToUSD(valueTotal, store?.paymentSettings?.currencyCode)}</Text></IndexTable.Cell>
                <IndexTable.Cell>
                  <Tooltip content={
                    <BlockStack gap="100">
                      <Text>
                        {`Gift Product Cost: ${numberToUSD(productCostTotal || 0, store?.paymentSettings?.currencyCode)}`}
                      </Text>
                      <Text>
                        {`Gift Shipping Cost: ${numberToUSD(shippingCostTotal || 0, store?.paymentSettings?.currencyCode)}`}
                      </Text>
                      <Text>
                        {`Commission: ${numberToUSD(commissionTotal, store?.paymentSettings?.currencyCode)}`}
                      </Text>
                      <Text>
                        {`Fixed Payments: ${numberToUSD(fixedPaymentsTotal || 0, store?.paymentSettings?.currencyCode)}`}
                      </Text>
                    </BlockStack>
                  }>
                    <Link removeUnderline>
                      {
                        numberToUSD(
                          (productCostTotal || 0)
                          + (shippingCostTotal || 0)
                          + (fixedPaymentsTotal || 0)
                          + (commissionTotal),
                          store?.paymentSettings?.currencyCode
                          // - (orderRow?.negativeCommission || 0)
                        )
                      }
                    </Link>
                  </Tooltip>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text color={rowProfit < 0 ? 'critical' : 'success'}>
                    {numberToUSD(rowProfit, store?.paymentSettings?.currencyCode)}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>{numOrders}</IndexTable.Cell>
                {/*<IndexTable.Cell>{lastPaidDate ? new Date(lastPaidDate)?.toLocaleDateString() : 'Never'}</IndexTable.Cell>*/}
              </IndexTable.Row>
            )
          }
        })
    console.log(creatorTimeSeriesOrders, 'ct orders')
    performanceGraph = (
      <PerformanceGraph
        timeSeriesData={creatorTimeSeriesOrders}
        labels={timeSeriesDates}
        title={`${typeTab === 0 ? 'Posts' : typeTab === 1 ? 'Engagements' : typeTab === 2 ? 'Clicks' : typeTab === 3 ? 'Revenue' : 'Cost'}`}
        subtitle={selectedCampaign === 'all' ? (<Text>All Campaigns</Text>) : <Link removeUnderline onClick={() => { navigate('/campaigns/' + selectedCampaign?.id) }}>{campaignsMap[selectedCampaign]?.campaignTitle}</Link>}
        dateStrings={
          dateRange === 'last7' ? 'Last 7 Days'
            : dateRange === 'last30' ? 'Last 30 Days'
              : dateRange === 'last90' ? 'Last 90 Days'
                : dateRange === 'allTime' ? 'All Time'
                  : `${selectedDates.start.toLocaleDateString()} - ${selectedDates.end.toLocaleDateString()}`}
      />
    )
  } else if (activeTab === 3) {
    rowMarkup = []
    headings = [
      { title: 'Product' },
      { title: 'Net Revenue' },
      { title: 'Quantity' },
      { title: 'Top Seller' },
    ]

    const groupedProducts = flatOrders.reduce((acc, order) => {
      order.products?.forEach(product => {
        const amount = parseFloat(product.value)
        if (!acc[product.title]) {
          acc[product.title] = {
            ...product,
            amount,
            byCreator: {
              [order.creatorID]: {
                creatorID: order.creatorID,
                quantity: product.quantity,
                amount,
              }
            }
          }
        } else {
          acc[product.title] = {
            ...acc[product.title],
            quantity: acc[product.title].quantity + product.quantity,
            amount: acc[product.title].amount + amount,
          }
          if (!acc[product.title].byCreator[order.creatorID]) {
            acc[product.title].byCreator[order.creatorID] = {
              creatorID: order.creatorID,
              quantity: product.quantity,
              amount,
            }
          } else {
            acc[product.title].byCreator[order.creatorID] = {
              creatorID: order.creatorID,
              quantity: acc[product.title].byCreator[order.creatorID].quantity + product.quantity,
              amount: acc[product.title].byCreator[order.creatorID].amount + amount,
            }
          }
        }
      })
      return acc
    }, {})

    /*
      productID: creatorID: {productCommission}
      */

    Object.values(groupedProducts).sort((a, b) => {
      return b.amount - a.amount
    }).forEach((product, index) => {
      const topCreatorID = Object.entries(product.byCreator).reduce((a, b) => a.amount > b.amount ? a : b)[0]
      const topCreator = leads?.find(creator => creator.id === topCreatorID)

      rowMarkup.push(
        <IndexTable.Row
          id={index}
          key={index}
          position={index}
        >
          <IndexTable.Cell>
            <InlineStack gap="200" blockAlign="center" align="start">
              <Avatar size="small" shape="square" source={product?.imageURL} initials={product?.title?.[0]} />
              {product.title}
            </InlineStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            {numberToUSD(product.amount, store?.paymentSettings?.currencyCode)}
          </IndexTable.Cell>
          <IndexTable.Cell>
            {product.quantity}
          </IndexTable.Cell>
          <IndexTable.Cell>
            <InlineStack gap="200" blockAlign="center" align="start">
              <Avatar size="small" shape="square" source={getBestAvatar(topCreator)} initials={topCreator?.name?.[0]} />
              {topCreator?.name}
            </InlineStack>
          </IndexTable.Cell>
        </IndexTable.Row>
      )
    })
  }


  return (
    <AppLayout>
      <BlockStack gap="500">
        <InlineStack align="space-between" gap="300" blockAlign="start">
          <BlockStack>
            <InlineStack gap="200" blockAlign="center" align="start">
              <InlineStack>
                <Icon source={ChartFunnelIcon} />
              </InlineStack>
              <Text variant="headingXl">Reports</Text>
            </InlineStack>
          </BlockStack>
          <InlineStack gap="300">
            <Select label="Campaign" labelInline options={[
              { label: 'All', value: 'all' },
              ...campaigns?.map(c => {
                return { label: c.campaignTitle, value: c.id }
              })]} value={selectedCampaign} onChange={v => setSelectedCampaign(v)} />
            <Select label="Date Range" labelInline options={[
              { label: 'Last 7 Days', value: 'last7' },
              { label: 'Last 30 Days', value: 'last30' },
              { label: 'Last 90 Days', value: 'last90' },
              { label: 'All Time', value: 'allTime' },
              { label: 'Custom', value: 'custom' },
            ]} value={dateRange} onChange={v => setDateRange(v)} />
            {
              /*
  <InlineStack gap="200">
  <Button icon={RedoMajor} loading={fetchOrderPerformanceLoading} onClick={fetchOrderPerformance} >Refresh</Button>
  </InlineStack>
  */
            }
          </InlineStack>
        </InlineStack>
        <BlockStack gap="200">
          <InlineGrid columns="1" gap="300">
            <BlockStack gap="200">
              <InlineGrid columns={{ xs: "2", md: "5" }} gap="300">
                <CategoryCards typeTab={typeTab} setTypeTab={setTypeTab} cards={[
                  {
                    title: 'Posts',
                    value: allMap.posts?.toLocaleString(),
                    icon: ImagesIcon,
                  },
                  {
                    title: 'Engagements',
                    value: shortenNumber(engagements),
                    icon: SmileyJoyIcon,
                  },
                  {
                    title: 'Clicks',
                    value: allMap.clicks?.toLocaleString(),
                    icon: CursorFilledIcon,
                  },
                  /*
{
title: 'Orders',
value: allMap.orders,
icon: OrderIcon,
},
*/
                  {
                    title: 'Revenue',
                    value: allMap.revenue ? numberToUSD(allMap.revenue, store?.paymentSettings?.currencyCode, 0) : '-',
                    icon: CashDollarIcon,
                  },
                  {
                    title: 'Cost',
                    value: numberToUSD(allMap.cost.gift + allMap.cost.commission + allMap.cost.fixed + allMap.cost.shipping, store?.paymentSettings?.currencyCode, 0),
                    icon: PaymentIcon,
                  },
                ]} />
              </InlineGrid>
              <BlockStack gap="300">
                <Card padding="0">
                  <BlockStack gap="4">
                    <Modal
                      title="Custom Date Range"
                      open={datePickerOpen}
                      onClose={() => setDatePickerOpen(false)}
                      primaryAction={{
                        content: 'Done',
                        onAction: () => setDatePickerOpen(false),
                      }}
                    >
                      <Box padding="3">
                        <DatePicker
                          month={month}
                          year={year}
                          onChange={setSelectedDates}
                          onMonthChange={handleMonthChange}
                          selected={selectedDates}
                          multiMonth
                          allowRange
                        />
                      </Box>
                    </Modal>
                  </BlockStack>
                </Card>
                <BlockStack>
                  <InlineGrid columns="1" gap="500">
                    <BlockStack>
                      {performanceGraph}
                    </BlockStack>
                    {
                      /*
  <BlockStack gap="300">
  <CategoryCards cards={[
  {
  title: 'ROI',
  value: (
  (allMap.revenue || 0) / (allMap.cost.gift + allMap.cost.commission + allMap.cost.fixed + allMap.cost.shipping)
  )?.toFixed(2) + 'x',
  icon: DatabaseIcon,
  },
  {
  title: 'AOV',
  value: (allMap.revenue) && allMap.orders ? numberToUSD((allMap.revenue || 0) / allMap.orders, store?.paymentSettings?.currencyCode, 0) : '-',
  icon: CartMajor,
  },
  {
  title: 'CPA',
  value: (allMap.cost.gift + allMap.cost.commission + allMap.cost.fixed + allMap.cost.shipping) && allMap?.orders ? numberToUSD((allMap.cost.gift + allMap.cost.commission + allMap.cost.fixed + allMap.cost.shipping) / allMap?.orders, store?.paymentSettings?.currencyCode, 0) : '-',
  icon: CustomerPlusMajor,
  }]} />
  </BlockStack>
  */
                    }
                  </InlineGrid>
                </BlockStack>
                <Card>
                  <IndexTable
                    resourceName={resourceName}
                    itemCount={rowMarkup?.length}
                    emptyState={<EmptyState heading="None Yet">
                      <Text>If you add affiliates, you'll see their performance here.</Text>
                    </EmptyState>}
                    // selectedItemsCount={
                    // allResourcesSelected ? 'All' : selectedResources.length
                    // }
                    // onSelectionChange={handleSelectionChange}
                    headings={headings?.map(h => {
                      return { id: h.title, title: renderHeading(h.title, h.icon) }
                    })
                    }
                    // bulkActions={bulkActions}
                    // promotedBulkActions={promotedBulkActions}
                    selectable={false}
                  >
                    {rowMarkup?.slice(page * rowsPerPage, (page + 1) * rowsPerPage)}
                  </IndexTable>
                  <InlineStack align="end">
                    <Select options={[
                      'Show 10',
                      'Show 25',
                      'Show 50',
                      'Show 100',
                    ]} value={'Show ' + rowsPerPage} onChange={v => {
                      setRowsPerPage(parseInt(v.split(' ')[1]))
                    }} />
                    <Pagination
                      hasPrevious={page > 0}
                      hasNext={(rowMarkup?.length) > (rowsPerPage * (page + 1))}
                      onPrevious={() => setPage(page - 1)}
                      onNext={() => setPage(page + 1)}
                    />
                  </InlineStack>
                </Card>
              </BlockStack>
            </BlockStack>
          </InlineGrid>
        </BlockStack>
      </BlockStack>
    </AppLayout>
  )
}
